/**
 * contentOpsService — Firestore CRUD for all content ops collections + Cloud Function invocations.
 */

import { db, storage } from '../firebase/config';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  deleteField,
  writeBatch
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions();

// ──────────────────────────────────────────
// Tweets
// ──────────────────────────────────────────

export async function getTweets(status = null, maxItems = 50) {
  let q;
  if (status) {
    q = query(
      collection(db, 'tweets'),
      where('status', '==', status),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );
  } else {
    q = query(
      collection(db, 'tweets'),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );
  }
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getPostedTweets(maxItems = 20) {
  const q = query(
    collection(db, 'tweets'),
    where('status', '==', 'posted'),
    orderBy('postedAt', 'desc'),
    limit(maxItems)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getQueuedTweets() {
  const q = query(
    collection(db, 'tweets'),
    where('status', '==', 'ready'),
    orderBy('createdAt', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function killTweet(tweetId) {
  await updateDoc(doc(db, 'tweets', tweetId), {
    status: 'killed',
    killedAt: Timestamp.now()
  });
}

export async function callGenerateTweet(contentId, style = 'breaking') {
  const fn = httpsCallable(functions, 'generateTweet');
  const result = await fn({ contentId, style });
  return result.data;
}

// ──────────────────────────────────────────
// Researched Content
// ──────────────────────────────────────────

export async function getResearchedContent(status = null, maxItems = 50) {
  let q;
  if (status) {
    q = query(
      collection(db, 'researched_content'),
      where('status', '==', status),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );
  } else {
    q = query(
      collection(db, 'researched_content'),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );
  }
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ──────────────────────────────────────────
// Generated Content (Content Studio)
// ──────────────────────────────────────────

export async function getGeneratedContent(format = null, maxItems = 50) {
  let q;
  if (format) {
    q = query(
      collection(db, 'generated_content'),
      where('format', '==', format),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );
  } else {
    q = query(
      collection(db, 'generated_content'),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );
  }
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * Fetch generated content with video_queue status merged.
 * For items with a videoQueueId, fetches the video_queue doc and merges video pipeline status.
 * Also fetches orphan video_queue items (those without a sourceContentId) like chess clips.
 */
export async function getGeneratedContentWithVideoStatus(maxItems = 100) {
  const q = query(
    collection(db, 'generated_content'),
    orderBy('createdAt', 'desc'),
    limit(maxItems)
  );
  const contentSnap = await getDocs(q);

  // Collect all videoQueueIds for batch lookup
  const items = contentSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  const videoQueueIds = items
    .filter(item => item.videoQueueId)
    .map(item => item.videoQueueId);

  // Batch fetch video_queue docs
  const videoStatusMap = {};
  if (videoQueueIds.length > 0) {
    // Firestore 'in' queries support max 30 items, so we chunk if needed
    const chunks = [];
    for (let i = 0; i < videoQueueIds.length; i += 30) {
      chunks.push(videoQueueIds.slice(i, i + 30));
    }
    for (const chunk of chunks) {
      const videoQuery = query(
        collection(db, 'video_queue'),
        where('__name__', 'in', chunk)
      );
      const videoSnap = await getDocs(videoQuery);
      videoSnap.docs.forEach(d => {
        videoStatusMap[d.id] = d.data();
      });
    }
  }

  // Merge video status into items
  const mergedItems = items.map(item => {
    if (item.videoQueueId && videoStatusMap[item.videoQueueId]) {
      const videoData = videoStatusMap[item.videoQueueId];
      // If video_queue is published, ensure item status reflects that
      const status = videoData.status === 'published' ? 'published' : item.status;
      return {
        ...item,
        status,
        videoStatus: videoData.status,
        voiceoverUrl: videoData.voiceoverUrl || null,
        avatarVideoUrl: videoData.avatarVideoUrl || null,
        brollMixedUrl: videoData.brollMixedUrl || null,
        caption: videoData.caption || item.caption || ''
      };
    }
    return item;
  });

  // Fetch orphan video_queue items (no sourceContentId) - includes chess clips
  const orphanQuery = query(
    collection(db, 'video_queue'),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  const orphanSnap = await getDocs(orphanQuery);

  // Filter to only orphans (no sourceContentId or sourceContentId is null)
  const orphanItems = orphanSnap.docs
    .filter(d => !d.data().sourceContentId)
    .map(d => {
      const data = d.data();
      // Map video_queue status to content status for filtering
      // "published" stays as "published", everything else is "draft"
      const contentStatus = data.status === 'published' ? 'published' : 'draft';
      return {
        id: d.id,
        // Map video_queue fields to content-like structure
        title: data.title || 'Untitled Video',
        body: data.description || data.script || '',
        topic: data.title,
        format: 'chess_clip',  // New format type for filtering
        status: contentStatus, // Use mapped status for filtering
        videoStatus: data.status,
        videoUrl: data.videoUrl,
        thumbnailUrl: data.thumbnailUrl,
        caption: data.caption || '', // Include caption
        createdAt: data.createdAt,
        // Mark as orphan video for UI differentiation
        isOrphanVideo: true,
        videoQueueId: d.id,
        // Include original video_queue data
        moves: data.moves,
        pgn: data.pgn,
        whitePlayer: data.whitePlayer,
        blackPlayer: data.blackPlayer,
        source: data.source,
      };
    });

  // Combine and sort by createdAt
  const allItems = [...mergedItems, ...orphanItems];
  allItems.sort((a, b) => {
    const aTime = a.createdAt?.toMillis?.() || 0;
    const bTime = b.createdAt?.toMillis?.() || 0;
    return bTime - aTime;
  });

  return allItems.slice(0, maxItems);
}

export async function updateGeneratedContent(id, updates) {
  await updateDoc(doc(db, 'generated_content', id), updates);
}

export async function deleteGeneratedContent(id) {
  await deleteDoc(doc(db, 'generated_content', id));
}

export async function callGenerateContent(topic, format, additionalContext = '') {
  const fn = httpsCallable(functions, 'generateContent');
  const result = await fn({ topic, format, additionalContext });
  return result.data;
}

export async function createManualContent(format, topic, body) {
  const ref = await addDoc(collection(db, 'generated_content'), {
    topic,
    body,
    format,
    status: 'draft',
    source: 'manual',
    version: 1,
    versions: [],
    images: [],
    selectedImageUrl: null,
    publishedChannels: [],
    publishedTo: [],
    createdAt: Timestamp.now(),
    publishedAt: null
  });
  return ref.id;
}

export async function callPublishToIG(imageUrl, caption, contentId = null) {
  const fn = httpsCallable(functions, 'publishToIG');
  const result = await fn({ imageUrl, caption, contentId });
  return result.data;
}

// ──────────────────────────────────────────
// Image Generation
// ──────────────────────────────────────────

export async function callGenerateImage(prompt, preset = 'social_card', contentId = null, referenceImage = null, previousImageUrl = null) {
  const fn = httpsCallable(functions, 'generateImage');
  const payload = { prompt, preset, contentId };

  // Add reference image if provided
  if (referenceImage) {
    payload.referenceImageBase64 = referenceImage.base64;
    payload.referenceImageMimeType = referenceImage.mimeType;
  }

  // Add previous image URL for refinement feedback loop
  if (previousImageUrl) {
    payload.previousImageUrl = previousImageUrl;
  }

  const result = await fn(payload);
  return result.data;
}

// ──────────────────────────────────────────
// Image Cropping
// ──────────────────────────────────────────

export async function callCropImage(sourceUrl, preset, contentId = null, outputFormat = 'jpeg') {
  const fn = httpsCallable(functions, 'cropImage');
  const result = await fn({ sourceUrl, preset, contentId, outputFormat });
  return result.data;
}

export async function callCropImageBatch(sourceUrl, presets, contentId = null, outputFormat = 'jpeg') {
  const fn = httpsCallable(functions, 'cropImageBatch', { timeout: 120000 });
  const result = await fn({ sourceUrl, presets, contentId, outputFormat });
  return result.data;
}

export async function callGetImagePresets() {
  const fn = httpsCallable(functions, 'getImagePresets');
  const result = await fn({});
  return result.data;
}

// ──────────────────────────────────────────
// Voiceover Generation
// ──────────────────────────────────────────

export async function callGenerateVoiceover(videoQueueId, voiceId = null) {
  const fn = httpsCallable(functions, 'generateVoiceover', { timeout: 120000 });
  const payload = { videoQueueId };
  if (voiceId) payload.voiceId = voiceId;
  const result = await fn(payload);
  return result.data;
}

export async function callGenerateAvatarVideo(videoQueueId) {
  const fn = httpsCallable(functions, 'generateAvatarVideo', { timeout: 540000 });
  const result = await fn({ videoQueueId });
  return result.data;
}

// ──────────────────────────────────────────
// Content Editor (Pipeline Editor)
// ──────────────────────────────────────────

export async function getContentById(id) {
  const snap = await getDoc(doc(db, 'generated_content', id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function callRespinContent(contentId, editedBody, feedback) {
  const fn = httpsCallable(functions, 'respinContent');
  const result = await fn({ contentId, editedBody, feedback });
  return result.data;
}

export async function callRespinOutreachEmail(emailId, editedBody, feedback) {
  const fn = httpsCallable(functions, 'respinOutreachEmail');
  const result = await fn({ emailId, editedBody, feedback });
  return result.data;
}

export async function callPublishContent(contentId, channels, author = 'TrendingChess AI', tags = []) {
  const fn = httpsCallable(functions, 'publishContent');
  const result = await fn({ contentId, channels, author, tags });
  return result.data;
}

/**
 * Publish video content to YouTube and Instagram via n8n webhook.
 * Convenience wrapper for video formats.
 */
export async function publishVideoContent(contentId, channels = ['youtube', 'instagram']) {
  return callPublishContent(contentId, channels);
}

export async function callRepurposeContent(contentId, targetFormat) {
  const fn = httpsCallable(functions, 'repurposeContent');
  const result = await fn({ contentId, targetFormat });
  return result.data;
}

export async function getRepurposedContent(parentId) {
  const q = query(
    collection(db, 'generated_content'),
    where('repurposedFrom', '==', parentId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ──────────────────────────────────────────
// Outreach
// ──────────────────────────────────────────

export async function getLeads(status = null, maxItems = 50) {
  let q;
  if (status) {
    q = query(
      collection(db, 'outreach_leads'),
      where('status', '==', status),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );
  } else {
    q = query(
      collection(db, 'outreach_leads'),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );
  }
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function updateLead(id, updates) {
  await updateDoc(doc(db, 'outreach_leads', id), updates);
}

export async function deleteLead(id) {
  await deleteDoc(doc(db, 'outreach_leads', id));
}

export async function getOutreachEmails(status = null, maxItems = 200) {
  let q;
  if (status) {
    q = query(
      collection(db, 'outreach_emails'),
      where('status', '==', status),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );
  } else {
    q = query(
      collection(db, 'outreach_emails'),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );
  }
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// Separate fetch for sent emails with a high limit — avoids being crowded out
// by the active drafts pool when getOutreachEmails has a shared cap
export async function getSentEmails(maxItems = 500) {
  const q = query(
    collection(db, 'outreach_emails'),
    where('status', '==', 'sent'),
    orderBy('createdAt', 'desc'),
    limit(maxItems)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function updateOutreachEmail(id, updates) {
  await updateDoc(doc(db, 'outreach_emails', id), updates);
}

export async function deleteOutreachEmail(id) {
  await deleteDoc(doc(db, 'outreach_emails', id));
}

export async function createOutreachEmail({ to, subject, body, leadId, leadName, template }) {
  const ref = await addDoc(collection(db, 'outreach_emails'), {
    recipientEmail: to || '',
    subject: subject || '',
    body: body || '',
    leadId: leadId || null,
    leadName: leadName || '',
    template: template || 'chessweb_pitch',
    status: 'draft',
    senderEmail: 'christopher@indiechess.com',
    source: 'manual',
    createdAt: Timestamp.now()
  });
  return ref.id;
}

export async function callFindLeads(limitCount = 20) {
  const fn = httpsCallable(functions, 'findLeads');
  const result = await fn({ limit: limitCount });
  return result.data;
}

export async function callDraftOutreachEmail(leadId, template = 'directory_listing') {
  const fn = httpsCallable(functions, 'draftOutreachEmail', { timeout: 60000 });
  const result = await fn({ leadId, template });
  return result.data;
}

export async function callCreateProfileForLead(leadId) {
  const fn = httpsCallable(functions, 'createProfileForLead', { timeout: 60000 });
  const result = await fn({ leadId });
  return result.data;
}

// ──────────────────────────────────────────
// Pipeline Runs (audit log)
// ──────────────────────────────────────────

export async function getPipelineRuns(pipeline = null, maxItems = 20) {
  let q;
  if (pipeline) {
    q = query(
      collection(db, 'pipeline_runs'),
      where('pipeline', '==', pipeline),
      orderBy('ranAt', 'desc'),
      limit(maxItems)
    );
  } else {
    q = query(
      collection(db, 'pipeline_runs'),
      orderBy('ranAt', 'desc'),
      limit(maxItems)
    );
  }
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ──────────────────────────────────────────
// Tweet Thread
// ──────────────────────────────────────────

export async function callPostTweetThread(contentId) {
  const fn = httpsCallable(functions, 'postTweetThread');
  const result = await fn({ contentId });
  return result.data;
}

// ──────────────────────────────────────────
// Email Sending
// ──────────────────────────────────────────

export async function callSendOutreachEmail(emailId, { bcc } = {}) {
  const fn = httpsCallable(functions, 'sendOutreachEmail');
  const payload = { emailId };
  if (bcc) payload.bcc = bcc;
  const result = await fn(payload);
  return result.data;
}

// ──────────────────────────────────────────
// Inbox (IMAP)
// ──────────────────────────────────────────

export async function callFetchInboxEmails(maxMessages = 50, folder = 'INBOX') {
  const fn = httpsCallable(functions, 'fetchInboxEmails');
  const result = await fn({ maxMessages, folder });
  return result.data;
}

export async function getInboxEmails(maxItems = 50) {
  const q = query(
    collection(db, 'inbox_emails'),
    orderBy('date', 'desc'),
    limit(maxItems)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ──────────────────────────────────────────
// Video Queue
// ──────────────────────────────────────────

export async function sendToVideoQueue(contentId) {
  const snap = await getDoc(doc(db, 'generated_content', contentId));
  if (!snap.exists()) throw new Error('Content not found');
  const content = snap.data();

  const creative = content.creativeSuggestions || {};

  // Support both new format (videoPacing) and legacy format (sections)
  let visualNotes = [];
  let videoPacing = content.videoPacing || content.scriptData?.videoPacing || [];

  // Extract visual notes from legacy sections if no videoPacing
  if (videoPacing.length === 0 && content.scriptData?.sections) {
    visualNotes = content.scriptData.sections
      .filter(s => s.type === 'visual')
      .map(s => s.note);
  }

  const queueDoc = {
    sourceContentId: contentId,
    // New format fields (top-level) take priority
    title: content.title || content.scriptData?.title || content.topic || 'Untitled',
    hook: content.hook || content.scriptData?.hook || null,
    script: content.body || '',
    spokenScript: content.spokenScript || content.scriptData?.spokenScript || null, // Clean narration for TTS
    caption: content.caption || '', // Social media caption for publishing
    videoPacing, // New format: scene-by-scene pacing with visual directions
    visualNotes, // Legacy: Array of visual/B-roll notes
    scriptData: content.scriptData || null, // Full structured script
    thumbnailIdeas: content.thumbnailIdeas || creative.thumbnailIdeas || [],
    remotionSuggestions: creative.remotion || null,
    imagePrompt: content.imagePrompt || creative.imagePrompt || null,
    selectedImageUrl: content.selectedImageUrl || null,
    status: 'queued',
    createdAt: Timestamp.now(),
    repurposedFrom: content.repurposedFrom || null
  };

  const ref = await addDoc(collection(db, 'video_queue'), queueDoc);

  await updateDoc(doc(db, 'generated_content', contentId), {
    videoQueueId: ref.id
  });

  return ref.id;
}

export async function getVideoQueue(status = null, maxItems = 50) {
  let q;
  if (status) {
    q = query(
      collection(db, 'video_queue'),
      where('status', '==', status),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );
  } else {
    q = query(
      collection(db, 'video_queue'),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );
  }
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function updateVideoQueueItem(id, updates) {
  await updateDoc(doc(db, 'video_queue', id), updates);
}

export async function deleteVideoQueueItem(id) {
  await deleteDoc(doc(db, 'video_queue', id));
}

export async function unarchiveVideoQueueItem(id) {
  await updateDoc(doc(db, 'video_queue', id), {
    status: deleteField(),
    archivedAt: deleteField()
  });
}

export async function getVideoQueueItemById(id) {
  const snap = await getDoc(doc(db, 'video_queue', id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function uploadSelfRecording(videoQueueId, file) {
  // 1. Upload video to Firebase Storage
  const ext = file.name.split('.').pop() || 'mp4';
  const path = `self-recordings/${videoQueueId}.${ext}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  const videoUrl = await getDownloadURL(storageRef);

  // 2. Call Cloud Function to process (STT for alignment)
  const fn = httpsCallable(functions, 'uploadSelfRecording', { timeout: 180000 });
  const result = await fn({ videoQueueId, videoUrl });
  return result.data;
}

/**
 * Upload a chess clip (rendered from Remotion ChessGameCustom)
 * @param {File} file - Video file to upload
 * @param {Object} metadata - Chess clip metadata
 * @param {string} metadata.title - Video title
 * @param {string} [metadata.description] - Optional description
 * @param {string[]} [metadata.moves] - Array of SAN moves
 * @param {string} [metadata.pgn] - Full PGN if available
 * @param {string} [metadata.source] - Source type (lichess, chess.com, opening, manual)
 * @param {string} [metadata.sourceUrl] - Original game URL
 * @param {string} [metadata.whitePlayer] - White player name
 * @param {string} [metadata.blackPlayer] - Black player name
 */
export async function uploadChessClip(file, metadata) {
  const { title } = metadata;
  if (!title) throw new Error('Title is required');

  // Generate a filename-safe version of the title
  const safeName = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 50);
  const timestamp = Date.now();
  const ext = file.name?.split('.').pop() || 'mp4';
  const storagePath = `chess-clips/${safeName}-${timestamp}.${ext}`;

  // 1. Upload to Firebase Storage
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file);
  const videoUrl = await getDownloadURL(storageRef);

  // 2. Call Cloud Function to register in video_queue
  const fn = httpsCallable(functions, 'uploadChessClip', { timeout: 60000 });
  const result = await fn({
    ...metadata,
    videoUrl,
    storagePath,
  });

  return result.data;
}

// ──────────────────────────────────────────
// Custom Image Upload
// ──────────────────────────────────────────

/**
 * Upload a custom image for content
 * @param {string} contentId - The content document ID
 * @param {File} file - Image file to upload
 * @returns {Promise<{imageUrl: string, storagePath: string}>}
 */
export async function uploadCustomImage(contentId, file) {
  const ext = file.name.split('.').pop() || 'png';
  const timestamp = Date.now();
  const storagePath = `content-images/${contentId}_${timestamp}.${ext}`;

  // Upload to Firebase Storage
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file);
  const imageUrl = await getDownloadURL(storageRef);

  // Update content document - add to images array and set as selected
  const contentRef = doc(db, 'generated_content', contentId);
  const snap = await getDoc(contentRef);
  const existingImages = snap.data()?.images || [];

  const newImage = {
    imageUrl,
    storagePath,
    source: 'upload',
    createdAt: new Date().toISOString()
  };

  await updateDoc(contentRef, {
    images: [...existingImages, newImage],
    selectedImageUrl: imageUrl
  });

  return { imageUrl, storagePath };
}

// ──────────────────────────────────────────
// Avatar Photo Management
// ──────────────────────────────────────────

export async function uploadAvatarPhoto(file) {
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    throw new Error('Only JPEG, PNG, and WebP images are allowed');
  }
  if (file.size > 5 * 1024 * 1024) {
    throw new Error('File must be under 5MB');
  }

  const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `avatars/${Date.now()}_${sanitizedName}`;
  const storageRef = ref(storage, path);

  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);

  const photo = { path, url, name: file.name, uploadedAt: Timestamp.now() };

  const configRef = doc(db, 'config', 'heygen');
  const configSnap = await getDoc(configRef);
  const existing = configSnap.exists() ? configSnap.data() : {};
  const uploadedPhotos = existing.uploadedPhotos || [];
  uploadedPhotos.push(photo);

  const update = { uploadedPhotos };
  // Auto-select as active if first upload
  if (!existing.activePhoto) {
    update.activePhoto = photo;
    update.talkingPhotoId = null;
    update.photoSource = null;
  }

  await setDoc(configRef, update, { merge: true });
  return photo;
}

export async function setActiveAvatarPhoto(photo) {
  const configRef = doc(db, 'config', 'heygen');
  await setDoc(configRef, {
    activePhoto: photo,
    talkingPhotoId: null,
    photoSource: null
  }, { merge: true });
}

export async function getAvatarConfig() {
  const configRef = doc(db, 'config', 'heygen');
  const snap = await getDoc(configRef);
  if (!snap.exists()) return { activePhoto: null, uploadedPhotos: [], talkingPhotoId: null };
  const data = snap.data();
  return {
    activePhoto: data.activePhoto || null,
    uploadedPhotos: data.uploadedPhotos || [],
    talkingPhotoId: data.talkingPhotoId || null
  };
}

// ──────────────────────────────────────────
// Tweet Compose & Approve-and-Post
// ──────────────────────────────────────────

export async function createTweetDraft(text, style = 'casual') {
  const ref = await addDoc(collection(db, 'generated_content'), {
    topic: text.slice(0, 80),
    body: text,
    format: 'tweet',
    style,
    status: 'draft',
    source: 'manual',
    tags: [],
    createdAt: Timestamp.now()
  });
  return ref.id;
}

export async function approveAndPublishTweet(contentId) {
  await changeContentStatus(contentId, 'approved');
  return await callPublishContent(contentId, ['twitter']);
}

// ──────────────────────────────────────────
// Daily Summary Stats
// ──────────────────────────────────────────

export async function getDailySummaryStats() {
  const today = new Date().toISOString().slice(0, 10);

  const [
    postedToday,
    tweetDrafts,
    draftEmails,
    draftContent,
    newLeads,
    researchedToday,
    blogDraftSnap
  ] = await Promise.all([
    getDocs(query(
      collection(db, 'pipeline_runs'),
      where('pipeline', '==', 'publishTweet'),
      where('dayKey', '==', today),
      where('status', '==', 'success')
    )),
    getDocs(query(
      collection(db, 'generated_content'),
      where('format', 'in', ['tweet', 'tweet_thread']),
      where('status', '==', 'draft')
    )),
    getDocs(query(
      collection(db, 'outreach_emails'),
      where('status', '==', 'draft')
    )),
    getDocs(query(
      collection(db, 'generated_content'),
      where('status', '==', 'draft')
    )),
    getDocs(query(
      collection(db, 'outreach_leads'),
      where('status', '==', 'new')
    )),
    getDocs(query(
      collection(db, 'researched_content'),
      orderBy('createdAt', 'desc'),
      limit(50)
    )),
    getDocs(query(
      collection(db, 'generated_content'),
      where('format', '==', 'blog'),
      where('status', '==', 'draft')
    ))
  ]);

  // Count items researched today
  const todayResearched = researchedToday.docs.filter(d => {
    const ts = d.data().createdAt;
    if (!ts) return false;
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toISOString().slice(0, 10) === today;
  }).length;

  return {
    tweetsPostedToday: postedToday.size,
    tweetDrafts: tweetDrafts.size,
    emailsDraft: draftEmails.size,
    contentDraft: draftContent.size,
    leadsNew: newLeads.size,
    researchedToday: todayResearched,
    blogDrafts: blogDraftSnap.size
  };
}

// ──────────────────────────────────────────
// Content Pipeline
// ──────────────────────────────────────────

export async function getContentByStatus(status, maxItems = 50) {
  const q = query(
    collection(db, 'generated_content'),
    where('status', '==', status),
    orderBy('createdAt', 'desc'),
    limit(maxItems)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function approveContent(contentId) {
  await updateDoc(doc(db, 'generated_content', contentId), {
    status: 'approved',
    approvedAt: Timestamp.now()
  });
}

export async function rejectContent(contentId) {
  await updateDoc(doc(db, 'generated_content', contentId), {
    status: 'rejected',
    rejectedAt: Timestamp.now()
  });
}

export async function archiveContent(contentId) {
  await updateDoc(doc(db, 'generated_content', contentId), {
    status: 'archived',
    archivedAt: Timestamp.now()
  });
}

export async function changeContentStatus(contentId, newStatus) {
  const update = { status: newStatus, [`${newStatus}At`]: Timestamp.now() };
  await updateDoc(doc(db, 'generated_content', contentId), update);
}

// ──────────────────────────────────────────
// Outreach CRM
// ──────────────────────────────────────────

export async function getLeadEmailHistory(leadId) {
  const q = query(
    collection(db, 'outreach_emails'),
    where('leadId', '==', leadId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getFollowUpQueue() {
  const leads = await getLeads(null, 200);
  const now = new Date();
  return leads.filter(lead => {
    if (!lead.lastContactedAt) return false;
    const lastContact = lead.lastContactedAt.toDate ? lead.lastContactedAt.toDate() : new Date(lead.lastContactedAt);
    const daysSince = (now - lastContact) / (1000 * 60 * 60 * 24);
    return (lead.status === 'contacted' && daysSince >= 3) ||
           (lead.status === 'followed_up' && daysSince >= 7);
  });
}

export async function callEnhanceTweet(text, style = 'breaking') {
  const fn = httpsCallable(functions, 'enhanceTweet');
  const result = await fn({ text, style });
  return result.data;
}

// ──────────────────────────────────────────
// Lead Import
// ──────────────────────────────────────────

export async function callImportLeads(leads) {
  const fn = httpsCallable(functions, 'importLeads');
  const result = await fn({ leads });
  return result.data;
}

export async function callImportResearchedContent(items) {
  const fn = httpsCallable(functions, 'importResearchedContent');
  const result = await fn({ items });
  return result.data;
}

export async function callImportGeneratedContent(items) {
  const fn = httpsCallable(functions, 'importGeneratedContent');
  const result = await fn({ items });
  return result.data;
}

// ──────────────────────────────────────────
// Dashboard Stats
// ──────────────────────────────────────────

export async function getContentOpsStats() {
  const today = new Date().toISOString().slice(0, 10);

  const [postedToday, tweetDrafts, draftEmails, newContent] = await Promise.all([
    getDocs(query(
      collection(db, 'pipeline_runs'),
      where('pipeline', '==', 'publishTweet'),
      where('dayKey', '==', today),
      where('status', '==', 'success')
    )),
    getDocs(query(
      collection(db, 'generated_content'),
      where('format', 'in', ['tweet', 'tweet_thread']),
      where('status', '==', 'draft')
    )),
    getDocs(query(
      collection(db, 'outreach_emails'),
      where('status', '==', 'draft')
    )),
    getDocs(query(
      collection(db, 'generated_content'),
      where('status', '==', 'draft')
    ))
  ]);

  return {
    tweetsPostedToday: postedToday.size,
    tweetDrafts: tweetDrafts.size,
    pendingEmails: draftEmails.size,
    draftContent: newContent.size
  };
}

// ──────────────────────────────────────────
// Calendar Bookings
// ──────────────────────────────────────────

export async function callSyncCalendarBookings(daysBack = 30, daysForward = 30) {
  const fn = httpsCallable(functions, 'syncCalendarBookings');
  const result = await fn({ daysBack, daysForward });
  return result.data;
}

export async function getCalendarBookings(filter = null, maxItems = 50) {
  const now = Timestamp.now();
  let q;

  if (filter === 'upcoming') {
    q = query(
      collection(db, 'calendar_bookings'),
      where('startTime', '>=', now),
      orderBy('startTime', 'asc'),
      limit(maxItems)
    );
  } else if (filter === 'past') {
    q = query(
      collection(db, 'calendar_bookings'),
      where('startTime', '<', now),
      orderBy('startTime', 'desc'),
      limit(maxItems)
    );
  } else if (filter === 'unlinked') {
    q = query(
      collection(db, 'calendar_bookings'),
      where('linkedLeadId', '==', null),
      orderBy('startTime', 'desc'),
      limit(maxItems)
    );
  } else {
    q = query(
      collection(db, 'calendar_bookings'),
      orderBy('startTime', 'desc'),
      limit(maxItems)
    );
  }

  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getCalendarSyncState() {
  const snap = await getDoc(doc(db, 'calendar_sync_state', 'sync_state'));
  if (!snap.exists()) return null;
  return snap.data();
}

export async function callLinkBookingToLead(bookingId, leadId) {
  const fn = httpsCallable(functions, 'linkBookingToLead');
  const result = await fn({ bookingId, leadId });
  return result.data;
}

export async function callUnlinkBookingFromLead(bookingId) {
  const fn = httpsCallable(functions, 'unlinkBookingFromLead');
  const result = await fn({ bookingId });
  return result.data;
}

export async function getConvertedLeads(maxItems = 50) {
  const q = query(
    collection(db, 'outreach_leads'),
    where('status', '==', 'converted'),
    orderBy('convertedAt', 'desc'),
    limit(maxItems)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getBookingStats() {
  const now = new Date();

  // Start of today
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);

  // Start of this week (Monday)
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1));
  weekStart.setHours(0, 0, 0, 0);

  // Start of this month
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  // Fetch all bookings from start of month (past + future)
  const q = query(
    collection(db, 'calendar_bookings'),
    where('startTime', '>=', Timestamp.fromDate(monthStart)),
    orderBy('startTime', 'asc')
  );

  const snap = await getDocs(q);
  const bookings = snap.docs.map(d => ({ id: d.id, ...d.data() }));

  // Count today
  const todayEnd = new Date(todayStart);
  todayEnd.setDate(todayEnd.getDate() + 1);
  const today = bookings.filter(b => {
    const bookingDate = b.startTime?.toDate?.() || new Date(b.startTime);
    return bookingDate >= todayStart && bookingDate < todayEnd;
  }).length;

  // Count this week
  const thisWeek = bookings.filter(b => {
    const bookingDate = b.startTime?.toDate?.() || new Date(b.startTime);
    return bookingDate >= weekStart;
  }).length;

  // Count this month (MTD)
  const thisMonth = bookings.length;

  // Find next upcoming booking
  const nextBooking = bookings.find(b => {
    const bookingDate = b.startTime?.toDate?.() || new Date(b.startTime);
    return bookingDate > now;
  });

  return { today, thisWeek, thisMonth, weekGoal: 5, nextBooking };
}

// ──────────────────────────────────────────
// Inbox Categorization
// ──────────────────────────────────────────

export async function callCategorizeInboxEmail(emailId, force = false) {
  const fn = httpsCallable(functions, 'categorizeInboxEmail');
  const result = await fn({ emailId, force });
  return result.data;
}

export async function callBatchCategorizeInboxEmails(limit = 20) {
  const fn = httpsCallable(functions, 'batchCategorizeInboxEmails', { timeout: 300000 });
  const result = await fn({ limit });
  return result.data;
}

export async function getUncategorizedEmails(maxItems = 50) {
  // Get emails without category field or with null category
  const q = query(
    collection(db, 'inbox_emails'),
    orderBy('date', 'desc'),
    limit(100)
  );
  const snap = await getDocs(q);
  const uncategorized = snap.docs
    .filter(d => !d.data().category)
    .slice(0, maxItems);
  return uncategorized.map(d => ({ id: d.id, ...d.data() }));
}

export async function getInboxReplies(maxItems = 50) {
  const q = query(
    collection(db, 'inbox_replies'),
    orderBy('createdAt', 'desc'),
    limit(maxItems)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getInboxEmailsByCategory(category, maxItems = 50) {
  const q = query(
    collection(db, 'inbox_emails'),
    where('category', '==', category),
    orderBy('date', 'desc'),
    limit(maxItems)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ──────────────────────────────────────────
// Outreach Analytics
// ──────────────────────────────────────────

export async function callAnalyzeOutreachPerformance(daysBack = 30, period = 'weekly') {
  const fn = httpsCallable(functions, 'analyzeOutreachPerformance', { timeout: 120000 });
  const result = await fn({ daysBack, period });
  return result.data;
}

export async function callGetConversionFunnel(daysBack = 30) {
  const fn = httpsCallable(functions, 'getConversionFunnel');
  const result = await fn({ daysBack });
  return result.data;
}

export async function getOutreachAnalytics(type = null, maxItems = 50) {
  let q;
  if (type) {
    q = query(
      collection(db, 'outreach_analytics'),
      where('type', '==', type),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );
  } else {
    q = query(
      collection(db, 'outreach_analytics'),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );
  }
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getOutreachInsights(maxItems = 10) {
  const q = query(
    collection(db, 'outreach_insights'),
    orderBy('createdAt', 'desc'),
    limit(maxItems)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ──────────────────────────────────────────
// Instagram Carousel Pipeline
// ──────────────────────────────────────────

export async function getCarouselQueue() {
  const fn = httpsCallable(functions, 'getCarouselQueue');
  const result = await fn({});
  return result.data;
}

export async function deleteCarouselItem(docId) {
  await deleteDoc(doc(db, 'ig_carousel_content', docId));
}

export async function getCarouselPipelineStats() {
  // Fetch last scheduled fetch and last carousel post from pipeline_runs
  const [fetchSnap, carouselSnap] = await Promise.all([
    getDocs(query(
      collection(db, 'pipeline_runs'),
      where('pipeline', 'in', ['scheduledTweetFetch', 'searchChessTweets']),
      orderBy('ranAt', 'desc'),
      limit(1)
    )),
    getDocs(query(
      collection(db, 'pipeline_runs'),
      where('pipeline', '==', 'importCarouselContent'),
      orderBy('ranAt', 'desc'),
      limit(1)
    ))
  ]);

  const lastFetchDoc = fetchSnap.docs[0]?.data();
  const lastCarouselDoc = carouselSnap.docs[0]?.data();

  return {
    lastFetch: lastFetchDoc?.ranAt?.toDate?.()?.toISOString() || lastFetchDoc?.ranAt || null,
    lastCarousel: lastCarouselDoc?.ranAt?.toDate?.()?.toISOString() || lastCarouselDoc?.ranAt || null
  };
}

// ──────────────────────────────────────────
// Engine Dashboard Stats
// ──────────────────────────────────────────

export async function getCarouselQueueCount() {
  const snap = await getDocs(collection(db, 'ig_carousel_content'));
  return snap.size;
}

export async function getUnreviewedCarouselCount() {
  const snap = await getDocs(query(
    collection(db, 'ig_carousel_content'),
    where('status', '==', 'pending')
  ));
  return snap.docs.filter(d => !d.data().reviewedAt).length;
}

export async function getCarouselCountByAccount(account) {
  const snap = await getDocs(query(
    collection(db, 'ig_carousel_content'),
    where('status', '==', 'pending')
  ));
  const pending = snap.docs.filter(d => {
    const postedTo = d.data().postedTo || [];
    return !postedTo.includes(account);
  });
  const unreviewed = pending.filter(d => !d.data().reviewedAt).length;
  return { total: pending.length, unreviewed };
}

/**
 * Get carousel counts broken down by content type (tweet, quote, widget_tweet).
 * Returns { tweets: { total, unreviewed }, quotes: { total, unreviewed }, widgetTweets: { total, unreviewed } }
 */
export async function getCarouselCountsByType() {
  const snap = await getDocs(query(
    collection(db, 'ig_carousel_content'),
    where('status', '==', 'pending')
  ));

  const counts = {
    tweets: { total: 0, unreviewed: 0 },
    quotes: { total: 0, unreviewed: 0 },
    widgetTweets: { total: 0, unreviewed: 0 },
  };

  snap.docs.forEach(d => {
    const data = d.data();
    const type = data.type;
    const reviewed = !!data.reviewedAt;

    if (type === 'tweet') {
      counts.tweets.total++;
      if (!reviewed) counts.tweets.unreviewed++;
    } else if (type === 'quote') {
      counts.quotes.total++;
      if (!reviewed) counts.quotes.unreviewed++;
    } else if (type === 'widget_tweet') {
      counts.widgetTweets.total++;
      if (!reviewed) counts.widgetTweets.unreviewed++;
    }
  });

  return counts;
}

/**
 * Get carousel counts by type AND by account (for per-engine breakdowns).
 * Returns { tweets: { total, unreviewed }, quotes: { total, unreviewed }, widgetTweets: { total, unreviewed } }
 * Only counts items not yet posted to the specified account.
 */
export async function getCarouselCountsByTypeAndAccount(account) {
  const snap = await getDocs(query(
    collection(db, 'ig_carousel_content'),
    where('status', '==', 'pending')
  ));

  const counts = {
    tweets: { total: 0, unreviewed: 0 },
    quotes: { total: 0, unreviewed: 0 },
    widgetTweets: { total: 0, unreviewed: 0 },
  };

  snap.docs.forEach(d => {
    const data = d.data();
    const postedTo = data.postedTo || [];
    if (postedTo.includes(account)) return; // already posted to this account

    const type = data.type;
    const reviewed = !!data.reviewedAt;

    if (type === 'tweet') {
      counts.tweets.total++;
      if (!reviewed) counts.tweets.unreviewed++;
    } else if (type === 'quote') {
      counts.quotes.total++;
      if (!reviewed) counts.quotes.unreviewed++;
    } else if (type === 'widget_tweet') {
      counts.widgetTweets.total++;
      if (!reviewed) counts.widgetTweets.unreviewed++;
    }
  });

  return counts;
}

/**
 * Get count of auto-rejected carousel items (for display in dashboard).
 */
export async function getAutoRejectedCarouselCount() {
  const snap = await getDocs(query(
    collection(db, 'ig_carousel_content'),
    where('status', '==', 'auto_rejected')
  ));
  return snap.size;
}

/**
 * Get full auto-rejected carousel items for review/restore.
 */
export async function getAutoRejectedCarouselItems() {
  const snap = await getDocs(query(
    collection(db, 'ig_carousel_content'),
    where('status', '==', 'auto_rejected')
  ));
  return snap.docs.map(d => {
    const data = { id: d.id, ...d.data() };
    if (data.createdAt && data.createdAt.toDate) {
      data.createdAt = data.createdAt.toDate().toISOString();
    }
    return data;
  });
}

/**
 * Restore an auto-rejected carousel item back to pending status.
 */
export async function restoreCarouselItem(docId) {
  await updateDoc(doc(db, 'ig_carousel_content', docId), {
    status: 'pending',
    rejectionReason: null,
    restoredAt: Timestamp.now()
  });
}

export async function approveCarouselItem(docId) {
  await updateDoc(doc(db, 'ig_carousel_content', docId), {
    reviewedAt: Timestamp.now()
  });
}

export async function bulkApproveCarouselItems(docIds) {
  const batch = writeBatch(db);
  docIds.forEach(id => {
    batch.update(doc(db, 'ig_carousel_content', id), {
      reviewedAt: Timestamp.now()
    });
  });
  await batch.commit();
}

// ──────────────────────────────────────────
// Daily Actions (Firestore-backed daily checkoffs)
// ──────────────────────────────────────────

export async function getDailyActions(dateKey) {
  const snap = await getDoc(doc(db, 'daily_actions', dateKey));
  return snap.exists() ? snap.data() : null;
}

export async function updateDailyAction(dateKey, field, value) {
  await setDoc(doc(db, 'daily_actions', dateKey), {
    [field]: value,
    updatedAt: Timestamp.now()
  }, { merge: true });
}

export async function getChessWebSocialStats() {
  const now = new Date();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1));
  weekStart.setHours(0, 0, 0, 0);

  // Use simple query to avoid missing composite index — filter client-side
  const q = query(
    collection(db, 'generated_content'),
    where('format', '==', 'chessweb_social'),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  const snap = await getDocs(q);
  const postsThisWeek = snap.docs.filter(d => {
    const data = d.data();
    if (data.status !== 'published' || !data.publishedAt) return false;
    const pub = data.publishedAt.toDate ? data.publishedAt.toDate() : new Date(data.publishedAt);
    return pub >= weekStart;
  }).length;
  return { postsThisWeek };
}

// ──────────────────────────────────────────
// ChessWeb Social Queue
// ──────────────────────────────────────────

export async function getChessWebSocialQueue() {
  // Simple query — no composite index needed. Filter client-side.
  const q = query(
    collection(db, 'chessweb_social_queue'),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(d => ['pending', 'selected', 'publishing', 'posted'].includes(d.status));
}

export async function selectChessWebVariation(docId, variationIndex) {
  const docRef = doc(db, 'chessweb_social_queue', docId);
  const snap = await getDoc(docRef);
  const editedText = snap.exists() ? snap.data().variations?.[variationIndex]?.text || '' : '';
  await updateDoc(docRef, {
    selectedVariation: variationIndex,
    editedText,
    selectedAt: Timestamp.now()
  });
}

export async function publishChessWebSocial(docId, editedText) {
  // Save edited text first
  await updateDoc(doc(db, 'chessweb_social_queue', docId), {
    editedText,
    status: 'publishing',
  });
  // Call Cloud Function
  const fn = httpsCallable(functions, 'publishChessWebSocial');
  const result = await fn({ docId, editedText });
  return result.data;
}

export async function deleteChessWebSocialItem(docId) {
  await deleteDoc(doc(db, 'chessweb_social_queue', docId));
}

export async function getChessWebSocialQueueCount() {
  // Simple query — no composite index needed
  const snap = await getDocs(collection(db, 'chessweb_social_queue'));
  return snap.docs.filter(d => {
    const s = d.data().status;
    return s === 'pending' || s === 'selected';
  }).length;
}

// ──────────────────────────────────────────
// Widget Tweet Queue
// ──────────────────────────────────────────

export async function getWidgetTweetQueue() {
  const q = query(
    collection(db, 'widget_tweet_queue'),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(d => ['pending', 'selected', 'publishing', 'posted'].includes(d.status));
}

export async function selectWidgetTweetVariation(docId, variationIndex) {
  const docRef = doc(db, 'widget_tweet_queue', docId);
  const snap = await getDoc(docRef);
  const editedText = snap.exists() ? snap.data().variations?.[variationIndex]?.text || '' : '';
  await updateDoc(docRef, {
    selectedVariation: variationIndex,
    editedText,
    selectedAt: Timestamp.now()
  });
}

export async function publishWidgetTweet(docId, editedText) {
  // Save edited text first
  await updateDoc(doc(db, 'widget_tweet_queue', docId), {
    editedText,
    status: 'publishing',
  });
  // Call Cloud Function
  const fn = httpsCallable(functions, 'publishWidgetTweet');
  const result = await fn({ docId, editedText });
  return result.data;
}

export async function deleteWidgetTweetItem(docId) {
  await deleteDoc(doc(db, 'widget_tweet_queue', docId));
}

/**
 * Publish a widget tweet directly from an ig_carousel_content item.
 * Creates a widget_tweet_queue doc, calls publishWidgetTweet Cloud Function,
 * then marks the carousel item as posted.
 */
export async function publishWidgetTweetFromCarousel(carouselDocId, carouselData) {
  // 1. Create a widget_tweet_queue doc with a single variation
  const queueRef = await addDoc(collection(db, 'widget_tweet_queue'), {
    widgetTitle: carouselData.widgetTitle || '',
    widgetSlug: carouselData.slug || '',
    widgetIcon: carouselData.widgetIcon || carouselData.icon || '',
    widgetLink: carouselData.url || '',
    variations: [{ text: carouselData.text, charCount: carouselData.text?.length || 0 }],
    status: 'selected',
    selectedVariation: 0,
    editedText: carouselData.text,
    createdAt: Timestamp.now(),
    context: 'Published from carousel queue',
  });

  // 2. Call the Cloud Function
  const fn = httpsCallable(functions, 'publishWidgetTweet');
  const result = await fn({ docId: queueRef.id, editedText: carouselData.text });

  // 3. Mark the carousel item as posted
  await updateDoc(doc(db, 'ig_carousel_content', carouselDocId), {
    reviewedAt: new Date().toISOString(),
    postBridgePublished: true,
  });

  return result.data;
}

export async function getWidgetTweetQueueCount() {
  const snap = await getDocs(collection(db, 'widget_tweet_queue'));
  return snap.docs.filter(d => {
    const s = d.data().status;
    return s === 'pending' || s === 'selected';
  }).length;
}

export async function getVideoQueueStats() {
  const snap = await getDocs(collection(db, 'video_queue'));
  const items = snap.docs.map(d => d.data());
  return {
    total: items.length,
    queued: items.filter(i => i.status === 'queued').length,
    voiceReady: items.filter(i => i.status === 'voice_ready').length,
    rendered: items.filter(i => i.status === 'rendered' || i.status === 'broll_mixed').length,
    published: items.filter(i => i.status === 'published').length,
  };
}

export async function getLatestOutreachInsight() {
  const q = query(
    collection(db, 'outreach_insights'),
    orderBy('createdAt', 'desc'),
    limit(1)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
}
